/**
 * useWritingSession — isolated coaching-session state machine.
 *
 * Implements show-not-tell/TS-004 (T3) and show-not-tell/US-004 (T2).
 * Owns: starting/resuming a session, restoring an active session on reload
 * (GET {id}), exchanging turns (respond), completing, cancelling, the
 * ordered message list, phase, loading/sending flags, and a single gentle
 * error state. This is the isolated brain of the /writing-practice surface —
 * it imports ONLY the slice service (services/showNotTell.ts); no
 * comprehension /sessions state or service is ever imported (BR-TS004.2 /
 * BR-009 slice isolation).
 *
 * Modelled on the React Query useMutation/onError lifecycle used by
 * useStartSession.ts / useCompleteSession.ts / useDashboard.ts.
 *
 * Reload/restore (AC-TS004.4 / AC-004.4 / edge case "Reload mid-session"):
 * the active sessionId is persisted in sessionStorage so a page reload calls
 * GET /{id} (restore) and NEVER calls start again.
 *
 * Gentle error mapping (AC-TS004.5 / AC-004.5) — enforces TO-LL-036's sibling
 * principle for user-facing errors (never leak a raw 5xx/stack to a child):
 * 5xx / network / model / moderation-failure -> "Something hiccuped. Let's
 * try that again." (kind: 'retry'); 402 INSUFFICIENT_COINS -> its
 * userMessage; 404 (Gate B lost mid-session) -> kind: 'close' (page
 * redirects to dashboard); 422 INPUT_BLOCKED / 400 EMPTY_DRAFT -> rendered as
 * an inline gentle coach message, not a hard global error. The 200-body
 * TURN_DEGRADED response is NOT an error — it renders as a normal degraded
 * coach turn.
 */

import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  startWritingSession,
  respondWritingTurn,
  completeWritingSession,
  cancelWritingSession,
  getWritingSession,
} from '@/services/showNotTell';
import type {
  WritingMessage,
  WritingVerdict,
  CompleteWritingResponse,
  RecentWritingResponse,
  RecentWritingSession,
} from '@/types/writingPractice';
import type { AuthError } from '@/types/auth';

// show-not-tell/TS-012 (T5, AC-TS012.6, TO-LL-026): the 500ms delay mirrors
// the documented in-repo TO-LL-026 fix — by the time it fires the
// eventually-consistent UserIndex GSI has propagated, so the reconciling
// refetch returns the fresh authoritative row in place of the optimistic one.
const RECENT_INVALIDATE_DELAY_MS = 500;

const SESSION_STORAGE_KEY = 'snt_active_session';

export type WritingSessionStatus = 'idle' | 'active' | 'completed';

export interface WritingGentleError {
  message: string;
  /** 'retry' shows a Try again action; 'close' signals the page to navigate home. */
  kind: 'retry' | 'close';
}

export interface UseWritingSessionReturn {
  messages: WritingMessage[];
  phase: string | undefined;
  status: WritingSessionStatus;
  isRestoring: boolean;
  isSending: boolean;
  isStarting: boolean;
  isOpeningSession: boolean;
  error: WritingGentleError | undefined;
  verdict: WritingVerdict | undefined;
  report: CompleteWritingResponse | undefined;
  start: (starterEmotion?: string) => Promise<void>;
  restore: () => Promise<void>;
  openSession: (sessionId: string) => Promise<void>;
  sendTurn: (text: string) => Promise<void>;
  complete: () => Promise<void>;
  cancel: () => Promise<void>;
  retry: () => Promise<void>;
}

function getStoredSessionId(): string | null {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeSessionId(id: string | null) {
  try {
    if (id) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch {
    // sessionStorage unavailable (e.g. private mode) — reload restore simply
    // will not work; not fatal to the current session.
  }
}

/**
 * Maps a propagated slice-service error to a single gentle, child-safe copy.
 * Implements show-not-tell/TS-004 (T3); enforces the AC-TS004.5/AC-004.5
 * "never a raw 5xx" rule and reuses the api.ts-normalized AuthError shape
 * (lib/systemErrorCodes.ts convention).
 */
function mapErrorToGentleCopy(err: unknown): WritingGentleError {
  const authErr = err as AuthError | undefined;
  const code = authErr?.code;

  if (code === 'INSUFFICIENT_COINS') {
    return { message: 'You need a coin to start a writing session.', kind: 'retry' };
  }
  if (code === 'NOT_FOUND') {
    // Gate B lost mid-session, or session unknown — page redirects to dashboard.
    return { message: 'This session is no longer available.', kind: 'close' };
  }
  if (code === 'TURN_COOLDOWN' || code === 'RATE_LIMITED') {
    return { message: 'One moment - take a breath and try again.', kind: 'retry' };
  }
  if (code === 'INPUT_BLOCKED') {
    return { message: "Let's keep it kind and safe, try another sentence.", kind: 'retry' };
  }
  if (code === 'EMPTY_DRAFT') {
    return {
      message: "Write your sentence first, then I'll help you make it shine.",
      kind: 'retry',
    };
  }
  // 5xx / network / model / moderation failure / anything unmapped: single
  // gentle retry-or-close banner — never a raw status/stack (AC-TS004.5).
  return { message: "Something hiccuped. Let's try that again.", kind: 'retry' };
}

export function useWritingSession(): UseWritingSessionReturn {
  // show-not-tell/TS-012 (T5, AC-TS012.6): queryClient for the completion-
  // transition optimistic prime + delayed conditional invalidate of
  // ['writing','recent'] (TO-LL-026). Never used for anything else here.
  const queryClient = useQueryClient();
  const [sessionId, setSessionId] = useState<string | null>(() => getStoredSessionId());
  // show-not-tell/TS-012 (T5): best-effort anchorEmotion tracker so the
  // completion-transition optimistic prime (below) can build a
  // RecentWritingSession row without waiting for the GSI. Neither
  // StartWritingResponse nor RespondWritingResponse currently returns
  // anchorEmotion (out of TS-012's scope to change those contracts); this
  // ref is updated whenever it IS known (the starterEmotion the child typed
  // at start, or the authoritative value on a restore/reload). It is
  // ONLY a display hint for the optimistic row — the delayed invalidate
  // (below) always reconciles with the authoritative backend value.
  const anchorEmotionRef = useRef<string>('');
  const [messages, setMessages] = useState<WritingMessage[]>([]);
  const [phase, setPhase] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<WritingSessionStatus>('idle');
  const [isRestoring, setIsRestoring] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isOpeningSession, setIsOpeningSession] = useState(false);
  const [error, setError] = useState<WritingGentleError | undefined>(undefined);
  const [verdict, setVerdict] = useState<WritingVerdict | undefined>(undefined);
  const [report, setReport] = useState<CompleteWritingResponse | undefined>(undefined);

  // Ref to the last failed action so retry() can re-invoke it (T3 step 8).
  const lastActionRef = useRef<(() => Promise<void>) | undefined>(undefined);

  const start = useCallback(async (starterEmotion?: string) => {
    setIsStarting(true);
    setError(undefined);
    lastActionRef.current = () => start(starterEmotion);
    try {
      const res = await startWritingSession(starterEmotion);
      setSessionId(res.sessionId);
      storeSessionId(res.sessionId);
      setPhase(res.phase);
      setStatus('active');
      setMessages([{ ...res.coachTurn, kind: 'prompt' }]);
      // show-not-tell/TS-012 (T5): best-effort anchor hint for the eventual
      // completion-transition optimistic prime (see complete() below).
      if (starterEmotion) {
        anchorEmotionRef.current = starterEmotion.trim();
      }
    } catch (err) {
      setError(mapErrorToGentleCopy(err));
    } finally {
      setIsStarting(false);
    }
  }, []);

  const restore = useCallback(async () => {
    const persistedId = getStoredSessionId();
    if (!persistedId) {
      // No active session — stay on the pre-start (idle) state. Never call
      // start on the reload path (AC-TS004.4 / edge case "Reload mid-session").
      return;
    }
    setIsRestoring(true);
    setError(undefined);
    lastActionRef.current = () => restore();
    try {
      const res = await getWritingSession(persistedId);
      setSessionId(res.sessionId);
      setPhase(res.phase);
      setMessages(res.messages);
      setStatus(res.status === 'completed' ? 'completed' : 'active');
      // show-not-tell/TS-012 (T5): authoritative anchorEmotion is known on
      // restore — refresh the best-effort hint used by complete()'s
      // optimistic prime.
      if (res.anchorEmotion) {
        anchorEmotionRef.current = res.anchorEmotion;
      }
    } catch (err) {
      const gentle = mapErrorToGentleCopy(err);
      if (gentle.kind === 'close') {
        // 404 (session gone / Gate B) — clear the stale id and reset to the
        // pre-start state rather than showing a persistent error.
        storeSessionId(null);
        setSessionId(null);
        setStatus('idle');
      } else {
        setError(gentle);
      }
    } finally {
      setIsRestoring(false);
    }
  }, []);

  /**
   * openSession — implements show-not-tell/US-011 (T-frontend-gap, AC-011.2).
   *
   * Closes a genuine functional gap left open by show-not-tell/TS-012: that
   * story's dev flagged that the recent-activity tile navigated every row to
   * the bare ROUTES.WRITING_PRACTICE start screen because the T4/T5 guide's
   * file_structure did not list a dedicated historical/read-only session
   * viewer. US-011's own "want" ("reopen a full session on the practice
   * page") and AC-011.2's given clause ("a student opening a SPECIFIC
   * session on the practice page ... so the page can render or RESTORE the
   * conversation") require the SPECIFIC clicked session's transcript to be
   * rendered, not merely a navigation to the generic page. The GET {id}
   * endpoint (API-006 / handle_get_session) already returns the full
   * transcript plus report fields for ANY owned session regardless of
   * status (TS-012/T2 docstring confirms this was true even before TS-012) —
   * so this hook only needed a caller that (a) takes an explicit id (not the
   * sessionStorage-persisted active id restore() reads) and (b) does not
   * clobber the in-progress-session storage key when the opened session is
   * already completed (view-only, nothing to resume).
   *
   * Enforces TO-LL-036 (never swallow) via the same mapErrorToGentleCopy
   * path restore() uses: a 404/403 (unknown id, or not owned) maps to
   * kind:'close' and the page redirects home instead of leaking a raw error.
   */
  const openSession = useCallback(async (targetSessionId: string) => {
    if (!targetSessionId) {
      return;
    }
    setIsOpeningSession(true);
    setError(undefined);
    lastActionRef.current = () => openSession(targetSessionId);
    try {
      const res = await getWritingSession(targetSessionId);
      setSessionId(res.sessionId);
      setPhase(res.phase);
      setMessages(res.messages);
      const isCompleted = res.status === 'completed';
      setStatus(isCompleted ? 'completed' : 'active');
      if (res.anchorEmotion) {
        anchorEmotionRef.current = res.anchorEmotion;
      }
      if (isCompleted) {
        // View-only: build a report-shaped object from GET {id}'s additive
        // revealed/result/summary fields (TS-012/T2) so the existing
        // "Nice work!" completed-view summary renders for a REOPENED past
        // session exactly as it does for a freshly-completed one, and the
        // reopened session never persists as the "active" session (nothing
        // to resume — reload restore() must still find no persisted id).
        setReport({
          status: res.status,
          summary: res.summary ?? undefined,
          report: res.result ?? undefined,
          coinDeducted: false,
        });
        storeSessionId(null);
      } else {
        // Non-completed (e.g. still active) session opened from history —
        // treat like a normal resume so it can be continued.
        storeSessionId(res.sessionId);
      }
    } catch (err) {
      const gentle = mapErrorToGentleCopy(err);
      if (gentle.kind === 'close') {
        // 404 unknown id / 403 not owned — never leak; go home (TO-LL-036).
        storeSessionId(null);
        setSessionId(null);
        setStatus('idle');
      } else {
        setError(gentle);
      }
    } finally {
      setIsOpeningSession(false);
    }
  }, []);

  const sendTurn = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !sessionId) {
        return;
      }
      // Optimistic child echo (< 100ms perceived latency).
      setMessages((prev) => [...prev, { role: 'child', text: trimmed, kind: 'tell' }]);
      setIsSending(true);
      setError(undefined);
      lastActionRef.current = () => sendTurn(text);
      try {
        const res = await respondWritingTurn(sessionId, trimmed);
        setPhase(res.phase);
        setVerdict(res.verdict);
        // TURN_DEGRADED (200, degraded:true) is a SUCCESS — render the
        // coach turn normally, never as an error (DD-3/DD-005).
        // BUG-20260717-003154 (RC-003, T-3): ordinary non-verdict
        // orient/explore/draft coach turns are NOT hints — leave kind
        // undefined so no label banner renders (matches the comprehension
        // chat, which has no per-turn label for ordinary coach prose).
        // BUG-20260718-125502 FIX 4 (2026-07-18): render the close turn as a
        // SINGLE bubble. Previously a revealed close appended the coach text
        // twice — once as 'feedback' (verdict present) and again as 'reveal' —
        // so the identical wrap-up/worked-example text showed under both a
        // "Feedback" and a "Here it is" label. The backend carries the close
        // text exactly once (in coachTurn); the duplication was purely this
        // client rendering it twice, so the fix lives here.
        //
        // SPRINT-8-ISS-001 fix (2026-07-24): res.revealed is only ever true
        // for the close_reveal action (show_not_tell_service.py
        // _force_close, `revealed = decision.action == 'close_reveal'`).
        // show-not-tell/TS-022 deleted the worked-example reveal AND the
        // second strong-tier call from that path entirely — close_reveal is
        // now ALWAYS the deterministic, honest, praise-free cap-close (its
        // own comment: 'degraded' and '_reveal_moderation_suppressed' are
        // always False here, there is nothing left on this path that could
        // degrade). So the old `res.degraded ? 'feedback' : 'reveal'`
        // selector always evaluated to 'reveal', making every honest,
        // non-completion close render under the celebratory "Here it is"
        // reveal treatment — contradicting TS-022's honest-close pedagogy.
        // The genuine win is a SEPARATE action (close_win) that always
        // returns revealed=false and is handled by the else branch below
        // (unchanged, still renders 'feedback' via res.verdict). There is no
        // remaining case where revealed=true should show 'reveal', so the
        // honest close now always renders as plain 'feedback' styling.
        if (res.revealed) {
          setMessages((prev) => [...prev, { ...res.coachTurn, kind: 'feedback' }]);
        } else {
          const kind = res.verdict ? 'feedback' : undefined;
          setMessages((prev) => [...prev, { ...res.coachTurn, kind }]);
        }
      } catch (err) {
        const gentle = mapErrorToGentleCopy(err);
        // 422 INPUT_BLOCKED / 400 EMPTY_DRAFT: render as an inline gentle
        // coach message rather than a hard global error; the session
        // continues.
        const authErr = err as AuthError | undefined;
        if (authErr?.code === 'INPUT_BLOCKED' || authErr?.code === 'EMPTY_DRAFT') {
          setMessages((prev) => [...prev, { role: 'coach', text: gentle.message, kind: 'hint' }]);
        } else if (gentle.kind === 'close') {
          storeSessionId(null);
          setSessionId(null);
          setStatus('idle');
        } else {
          setError(gentle);
        }
      } finally {
        setIsSending(false);
      }
    },
    [sessionId]
  );

  const complete = useCallback(async () => {
    if (!sessionId) {
      return;
    }
    setError(undefined);
    lastActionRef.current = () => complete();
    try {
      const res = await completeWritingSession(sessionId);
      setReport(res);
      setStatus('completed');
      if (res.summary) {
        setMessages((prev) => [...prev, { role: 'coach', text: res.summary!, kind: 'feedback' }]);
      }
      storeSessionId(null);

      // show-not-tell/TS-012 (T5, AC-TS012.6, TO-LL-026): on the
      // COMPLETION TRANSITION ONLY (never after a respond turn) — prime
      // the ['writing','recent'] tile cache optimistically with the
      // just-completed session so it is visible IMMEDIATELY, independent
      // of the UserIndex GSI's eventual-consistency lag (ConsistentRead is
      // illegal on that GSI; the base-table get_session ConsistentRead in
      // TS-012/T1-T2 covers the {id} full-session read only, not this
      // tile). Then schedule a delayed, conditional reconciliation refetch
      // — by the time it fires the GSI has propagated and the authoritative
      // row replaces the optimistic one. Both steps are best-effort UI
      // refresh: the child has already completed successfully, so a
      // failure here must NEVER throw out of complete() (TO-LL-036).
      try {
        const signals = (res.report as { successSignals?: Record<string, unknown> } | undefined)
          ?.successSignals;
        const justCompleted: RecentWritingSession = {
          sessionId,
          anchorEmotion: anchorEmotionRef.current || '',
          createdAt: new Date().toISOString(),
          revealFired: Boolean(signals?.revealFired),
          concreteLayerCountAtFirstPass:
            typeof signals?.concreteLayerCountAtFirstPass === 'number'
              ? signals.concreteLayerCountAtFirstPass
              : null,
          roundsToResolve: typeof signals?.roundsToResolve === 'number' ? signals.roundsToResolve : 0,
        };
        queryClient.setQueryData<RecentWritingResponse>(['writing', 'recent'], (old) => ({
          sessions: [justCompleted, ...(old?.sessions ?? []).filter((s) => s.sessionId !== sessionId)],
        }));
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['writing', 'recent'] }).catch(() => {
            // Best-effort reconciliation only — never rethrown (TO-LL-036).
          });
        }, RECENT_INVALIDATE_DELAY_MS);
      } catch {
        // Best-effort UI refresh only — completion has already succeeded;
        // never let a cache-priming failure surface as a session error.
      }
    } catch (err) {
      setError(mapErrorToGentleCopy(err));
    }
  }, [sessionId, queryClient]);

  const cancel = useCallback(async () => {
    if (!sessionId) {
      storeSessionId(null);
      setStatus('idle');
      return;
    }
    try {
      await cancelWritingSession(sessionId);
    } catch (err) {
      // Cancel is best-effort from the UI's perspective — log for developers
      // but still clear local state so the child is never stuck.
      console.error('cancelWritingSession failed', err);
    } finally {
      storeSessionId(null);
      setSessionId(null);
      setStatus('idle');
      setMessages([]);
      setPhase(undefined);
    }
  }, [sessionId]);

  const retry = useCallback(async () => {
    setError(undefined);
    if (lastActionRef.current) {
      await lastActionRef.current();
    }
  }, []);

  return {
    messages,
    phase,
    status,
    isRestoring,
    isSending,
    isStarting,
    isOpeningSession,
    error,
    verdict,
    report,
    start,
    restore,
    openSession,
    sendTurn,
    complete,
    cancel,
    retry,
  };
}
